import React, { useState } from "react";
import CrudPage from "@/components/common/CrudPage";
import { Users } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import { useAIOutputs } from "@/hooks/useAIOutputs";
import AICategoryPanel from "@/components/ai/AICategoryPanel";

const STRENGTHS = [
  { value: "strong", label: "Strong (Ισχυρός για υπεράσπιση)" },
  { value: "neutral", label: "Neutral (Ουδέτερος)" },
  { value: "risky", label: "Risky (Επισφαλής)" },
  { value: "hostile", label: "Hostile (Εχθρικός)" },
];

const FIELDS = [
  { k: "full_name", label: "Ονοματεπώνυμο" },
  { k: "role", label: "Ιδιότητα", ph: "π.χ. Αυτόπτης μάρτυρας / Παθών" },
  { k: "relationship", label: "Σχέση με υπόθεση" },
  { k: "strength", label: "Δείκτης μάρτυρα", type: "select", options: STRENGTHS },
  { k: "statement_summary", label: "Σύνοψη κατάθεσης", type: "textarea", rows: 3 },
  { k: "key_claims", label: "Βασικοί ισχυρισμοί", type: "textarea", rows: 3 },
  { k: "contradictions", label: "Αντιφάσεις", type: "textarea", rows: 3 },
  { k: "credibility_issues", label: "Ζητήματα αξιοπιστίας", type: "textarea", rows: 2 },
  { k: "helpful_points", label: "Ευνοϊκά σημεία", type: "textarea", rows: 2 },
  { k: "harmful_points", label: "Επιβαρυντικά σημεία", type: "textarea", rows: 2 },
  { k: "cross_questions", label: "Ερωτήσεις αντεξέτασης", type: "textarea", rows: 4 },
  { k: "source_documents", label: "Πηγές / Έγγραφα", type: "textarea", rows: 2 },
  { k: "notes", label: "Σημειώσεις", type: "textarea", rows: 2 },
];

function WitnessAIPanels({ witnesses }) {
  const { currentCase } = useApp();
  const { outputs: aiOutputs, addOutput: addAIOutput } = useAIOutputs(currentCase?.id);
  const [focusWitness, setFocusWitness] = useState("");

  return (
    <div className="space-y-4">
      <AICategoryPanel
        caseId={currentCase?.id} promptKey="witness_contradictions" titleEl="Αντιφάσεις Μαρτύρων (AI)"
        outputs={aiOutputs} onGenerated={addAIOutput}
      />
      <div>
        <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">
          Εστίαση σε μάρτυρα (για αντεξέταση AI)
        </label>
        <select value={focusWitness} onChange={e => setFocusWitness(e.target.value)}
          data-testid="witness-ai-focus-select"
          className="mt-1.5 mb-3 w-full bg-muted/30 border border-border/80 rounded-md px-3 py-2 text-[13px]">
          <option value="">—</option>
          {witnesses.map(w => <option key={w.id} value={w.id}>{w.full_name}</option>)}
        </select>
        <AICategoryPanel
          caseId={currentCase?.id} promptKey="cross_examination" titleEl="Αντεξέταση Μάρτυρα (AI)"
          outputs={aiOutputs} onGenerated={addAIOutput} witnessId={focusWitness || null}
        />
      </div>
    </div>
  );
}

export default function Witnesses() {
  return (
    <CrudPage
      slug="witnesses"
      title="Μάρτυρες"
      subtitle="Προφίλ μαρτύρων, δείκτης ισχύος, αντιφάσεις, και ερωτήσεις αντεξέτασης."
      emptyIcon={Users}
      fields={FIELDS}
      defaultItem={{ strength: "neutral" }}
      listColumns={{
        title: it => it.full_name,
        subtitle: it => it.role,
        body: it => it.statement_summary,
      }}
      badges={{
        strength: { field: "strength", labelMap: { strong: "ΙΣΧΥΡΟΣ", neutral: "ΟΥΔΕΤΕΡΟΣ", risky: "ΕΠΙΣΦΑΛΗΣ", hostile: "ΕΧΘΡΙΚΟΣ" } },
      }}
      extra={witnesses => <WitnessAIPanels witnesses={witnesses}/>}
    />
  );
}
